// @flow
import React, { Fragment, useContext, useState } from 'react';
import classnames from 'classnames';

import { getLinkProps, getLinkTagName } from 'Components/utils/markup/markup';
import ratios from 'Components/utils/ratios/ratios.utils';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { type LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList.jsx';
import { TeaserCard } from 'Components/frameworks/teaser-card/TeaserCard.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

export type ProductRoomCardProps = {
  className?: string,
  labeledIconsList?: Array<LabeledIcon>,
  isExclusiveCollection?: boolean,
  isHidden?: boolean,
  isLarge?: boolean,
  link?: {
    onClick?: () => *,
    target?: string,
    url?: string,
  },
  media: Array<{ alt?: string, src: string }>,
  seeMoreLabel?: string,
  title: string,
  trackingData?: TrackingData,
  width?: number,
};

export function ProductRoomCard({
  className,
  isExclusiveCollection,
  isHidden,
  isLarge,
  labeledIconsList = [],
  link = {},
  media = [],
  seeMoreLabel,
  title,
  trackingData,
  width,
}: ProductRoomCardProps) {
  const [isHovering, setHovering] = useState(false);
  const { isDesktop, isTablet } = useContext(ResponsiveContext);

  const tagName = getLinkTagName(link);
  const linkProps = getLinkProps(link);

  const content = (
    <Fragment>
      <Title
        className="my-4"
        hasEllipsis
        theme={isExclusiveCollection ? 'title-serif-3' : 'title-sans-3'}
      >
        {title}
      </Title>
      <ul className={classnames('reset-list my-4', { 'lg:-mx-1 lg:flex lg:flex-wrap': isLarge })}>
        {labeledIconsList.map(({ icon, labels }, index) => (
          <li
            className={classnames('flex justify-between mt-1 overflow-hidden text-base', {
              'lg:flex-none lg:w-1/2 px-1': isLarge,
            })}
            key={index}
            style={{ height: '1.25rem' }}
          >
            {!!labels && (
              <span className="inline-flex items-center max-w-4/5">
                <Icon className="flex-shrink-0 rtl:mr-2" name={icon} width="1em" />
                <span className="truncate">
                  {labels.map(label => {
                    return (
                      <span key={label}>
                        {label}
                        &nbsp;
                      </span>
                    );
                  })}
                </span>
              </span>
            )}
          </li>
        ))}
      </ul>
      {!!seeMoreLabel && (
        <p className="reset-paragraph my-4 text-center text-sm underline">{seeMoreLabel}</p>
      )}
    </Fragment>
  );

  const ratio = isLarge && isTablet ? ratios.R_38_BY_58 : ratios.R_38_BY_28;

  return isDesktop ? (
    <TeaserCard
      aria-hidden={isHidden}
      className={classnames(className, 'transition-transform w-full')}
      hasInsetBorder={isExclusiveCollection}
      isActive={isHovering}
      media={media}
      nextLabel={t('common.next')}
      previousLabel={t('common.previous')}
      ratio={ratio}
      width={width}
    >
      <TrackClick
        {...linkProps}
        as={tagName}
        className="reset-button text-initial w-full"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        tabIndex={isHidden ? -1 : 0}
        trackingData={trackingData}
      >
        {content}
      </TrackClick>
    </TeaserCard>
  ) : (
    <TrackClick
      {...linkProps}
      aria-hidden={isHidden}
      as={tagName}
      className={classnames(className, 'reset-button block text-initial w-full')}
      style={{ width }}
      tabIndex={isHidden ? -1 : 0}
      trackingData={trackingData}
    >
      <TeaserCard hasInsetBorder={isExclusiveCollection} media={media.slice(0, 1)} ratio={ratio}>
        {content}
      </TeaserCard>
    </TrackClick>
  );
}
